import { signOut } from 'aws-amplify/auth';
import { AxiosResponse } from 'axios';
import { defineStore } from 'pinia';

import api from '@/api/api';
import { IAffordabilityResponse } from "@/api/mocks/affordability/affordabilityResponse";
import {
  AFFORDABILITY_REFRESH_URL,
  AUTH_CC_SESSION_URL,
  AUTH_LOGOUT_URL,
  DELETE_URL,
  KBA_QUESTIONS_URL,
  KBA_VERIFY_URL,
  MORTGAGE_URL,
  RECONFIRM_DETAILS_URL,
  REF_DATA_URL,
  SEND_OTP_URL,
  VERIFY_OTP_URL
} from '@/constants/urls';
import { CONFIRM_DATA_ANSWERS, LOG_PREFIX } from '@/types/enums';
import { KbaAnswer, KbaQuestionsResponse, KbaVerifyResponse } from '@/types/kba';
import { SendOtpResponse, VerifyOtpResponse } from '@/types/otp';
import {
  LoanCalcDefaultValues,
  MortgageCalculatorResults,
} from '@/types/responseTypes';
import { CardSummary, JobItem } from '@/types/types';
import {
  MortgageCalculatorForm,
} from '@/types/validationSchemaTypes';
import { getAuthToken } from '@/utils/helperFunctions';

interface State {
  registrationUsername: string
  registrationEmail: string
  occupations: JobItem[]
  businessSectors: JobItem[]
  topCardsRequestAttempts: number
  summaryTopCards: CardSummary[]
  isSavedUserSettings: boolean
  mortgageResults: MortgageCalculatorResults | null
  loansDefaultFormValues: LoanCalcDefaultValues | null
  otpInfo: {
    sendAttemptsLeft: number | null,
    mobilePhoneEnding: string | null,
  },
}

export default defineStore('user', {
  state: (): State => ({
    registrationUsername: '',
    registrationEmail: '',
    occupations: [],
    businessSectors: [],
    topCardsRequestAttempts: 0,
    summaryTopCards: [],
    isSavedUserSettings: true,
    mortgageResults: null,
    loansDefaultFormValues: null,
    otpInfo: {
      sendAttemptsLeft: null,
      mobilePhoneEnding: '',
    },
  }),
  actions: {
    async ccSessionAuth(idToken?: string) {
      const token = idToken ?? await getAuthToken();

      await api({
        url: AUTH_CC_SESSION_URL,
        method: 'post',
        logPrefix: LOG_PREFIX.CC_SESSION_AUTH,
        headers: {
          authorization: token ? `Bearer ${token}` : '',
        },
        data: JSON.stringify({ token }),
      });
    },
    async logout() {
      const token = await getAuthToken();

      await api({
        url: AUTH_LOGOUT_URL,
        method: 'post',
        logPrefix: LOG_PREFIX.LOGOUT_AUTH,
        data: JSON.stringify({ token }),
      });

      try {
        await signOut();
      } catch (authError) {
        window.$log.warn(`[${LOG_PREFIX.LOGOUT_AUTH}]: signOut failed`, { error: authError });
      }
    },

    async getOccupations(query: string): Promise<JobItem[]> {
      try {
        const res: AxiosResponse<{ value: number; label: string }[]> = await api({
          url: `${REF_DATA_URL}/occupations`,
          method: 'get',
          params: {
            query,
          },
          logPrefix: LOG_PREFIX.OCCUPATIONS,
        });

        const result = res.data.map(item => ({ id: item.value, value: item.label }));
        this.occupations = result;
        return result;
      } catch (e) {
        this.occupations = [];
        throw e;
      }
    },

    async getBusinessSectors(query: string): Promise<JobItem[]> {
      try {
        const res: AxiosResponse<{ value: number; label: string }[]> = await api({
          url: `${REF_DATA_URL}/business-sectors`,
          method: 'get',
          params: {
            query,
          },
          logPrefix: LOG_PREFIX.BUSINESS_SECTORS,
        });

        const result = res.data.map(item => ({ id: item.value, value: item.label }));
        this.businessSectors = result;
        return result;
      } catch (e) {
        this.businessSectors = [];
        throw e;
      }
    },

    async calculateMortgage(values: MortgageCalculatorForm) {
      const data = {
        applicants: [
          {
            annualIncome: values.annualIncome,
            ...(values.bonusesAmount && {
              bonusesOvertimeCommission: values.bonusesAmount,
            }),
            ...(values.governmentBenefitsAmount && {
              governmentBenefits: values.governmentBenefitsAmount,
            }),
            ...(values.anyOtherIncomeAmount && {
              guaranteedIncome: values.anyOtherIncomeAmount,
            }),
          },

          ...((values.isJointApplication === CONFIRM_DATA_ANSWERS.YES && [
            {
              annualIncome: values.applicantsAnnualIncome,
              ...(values.applicantsBonusesAmount && {
                bonusesOvertimeCommission: values.applicantsBonusesAmount,
              }),
              ...(values.applicantsGovernmentBenefitsAmount && {
                governmentBenefits: values.applicantsGovernmentBenefitsAmount,
              }),
              ...(values.applicantsAnyOtherIncomeAmount && {
                guaranteedIncome: values.applicantsAnyOtherIncomeAmount,
              }),
            },
          ]) ||
            []),
        ],
        mortgagePreferences: {
          willLiveInProperty: true,
          isFirstTimeBuyer: values.isFirstTimeBuyer === CONFIRM_DATA_ANSWERS.YES,
          isFlatPurchase: values.consideringBuyingFlat === CONFIRM_DATA_ANSWERS.YES,
          deposit: values.depositAmount,
        },
        combinedCreditCardsOverdraft: values.outstandingBalances,
        ...(values.otherMajorCommitmentsAmount && {
          otherMajorMonthlyCommitments: values.otherMajorCommitmentsAmount,
        }),
        requiredTerm: values.yearsOfBorrowing,
      };

      return api<MortgageCalculatorResults>({
        url: MORTGAGE_URL,
        method: 'post',
        data,
        logPrefix: LOG_PREFIX.MORTGAGE
      });
    },

    async getMortgagesData(): Promise<void> {
      try {
        const { data } = await api<MortgageCalculatorResults>({
          url: MORTGAGE_URL,
          method: 'get',
          logPrefix: LOG_PREFIX.MORTGAGE,
        });

        if (data) {
          const redirectUrl = `${data.redirectUrl}&propertyValue=${
            data.safeBorrowingAmount.suggestedPropertyValue
          }&mortgageAmount=${(
            +data.safeBorrowingAmount.suggestedPropertyValue - data.deposit
          ).toFixed(2)}`;

          this.mortgageResults = {
            ...data,
            redirectUrl,
          };
        } else this.mortgageResults = null;
      } catch {
        this.mortgageResults = null;
      }
    },

    async deleteAccount(): Promise<AxiosResponse> {
      return api({
        url: DELETE_URL,
        method: 'delete',
        logPrefix: LOG_PREFIX.DELETE_ACCOUNT,
      });
    },

    async reconfirmDetails(): Promise<AxiosResponse> {
      return api({
        url: RECONFIRM_DETAILS_URL,
        method: 'post',
        data: null,
        headers: {
          'Content-Type': 'application/json',
        },
        logPrefix: LOG_PREFIX.RECONFIRM_DETAILS,
      });
    },

    async getKbaQuestions(): Promise<AxiosResponse<KbaQuestionsResponse>> {
      return api({
        url: KBA_QUESTIONS_URL,
        method: 'get',
        skipLogging: true,
      });
    },

    async verifyKbaAnswers(answers: KbaAnswer[]): Promise<AxiosResponse<KbaVerifyResponse>> {
      return api({
        url: KBA_VERIFY_URL,
        method: 'post',
        data: {
          items: answers,
          userDevice: {
            blackBox: window.IGLOO.getBlackbox().blackbox,
          }
        },
        skipLogging: true,
      });
    },

    async sendOtp(): Promise<AxiosResponse<SendOtpResponse>> {
      const response = await api({
        url: SEND_OTP_URL,
        method: 'post',
        skipLogging: true,
      }) as AxiosResponse<SendOtpResponse>;

      if (response.data.status) {
        this.otpInfo.sendAttemptsLeft = response.data.sendAttemptsLeft;
        this.otpInfo.mobilePhoneEnding = response.data.mobilePhoneEnding;
      }

      return response;
    },

    async verifyOtp(passcode: string): Promise<AxiosResponse<VerifyOtpResponse>> {
      return api({
        url: `${VERIFY_OTP_URL}/${passcode}`,
        method: 'get',
        skipLogging: true,
      });
    },

    async refreshAffordability(): Promise<AxiosResponse<IAffordabilityResponse>> {
      return api({
        url: AFFORDABILITY_REFRESH_URL,
        method: 'post',
        logPrefix: LOG_PREFIX.AFFORDABILITY_REFRESH,
      });
    }
  },
});
